import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Zap, Phone, Brain, Target, TrendingUp, Clock, Users, BarChart3, MessageSquare, Shield, Award, CheckCircle, Headphones, Bell, GraduationCap, Car, Building2, Gauge, LineChart, PhoneCall, PhoneIncoming, PhoneOutgoing, AlertTriangle, DollarSign, Rocket } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "The Complete",
      highlight: "Phone Intelligence",
      subhead: "Platform",
      tagline: "Speed-to-Lead + Call Analytics + AI Coaching"
    }
  },
  {
    id: 2,
    type: "problem",
    content: {
      label: "THE REALITY",
      headline: "Every Call Is a Moment of Truth",
      stats: [
        { value: "60%+", label: "of buyers call after researching online" },
        { value: "8x", label: "higher conversion when you respond in 5 min" },
        { value: "64%", label: "of leads die from neglect" },
      ],
      punchline: "What happens on that call determines if you win or lose the deal."
    }
  },
  {
    id: 3,
    type: "speedToLead",
    content: {
      label: "SPEED TO LEAD",
      headline: "The First to Respond Wins",
      comparison: [
        { time: "Under 2 min", result: "3x more likely to set appointment", color: "#22c55e" },
        { time: "Under 5 min", result: "2x more likely to close the sale", color: "#3b82f6" },
        { time: "Over 5 min", result: "400% drop in conversion", color: "#ef4444" },
      ],
      punchline: "Speed isn't just an advantage. It's everything."
    }
  },
  {
    id: 4,
    type: "twoSides",
    content: {
      label: "THE COMPLETE SOLUTION",
      headline: "Outbound + Inbound Intelligence",
      sideA: {
        title: "Speed-to-Lead",
        subtitle: "Outbound Response",
        icon: "phoneOutgoing",
        color: "#f97316",
        items: [
          "Instant lead routing (under 10 seconds)",
          "AI-powered after-hours response",
          "Automated follow-up sequences",
          "First-to-call advantage"
        ]
      },
      sideB: {
        title: "Call Analytics",
        subtitle: "Inbound Intelligence",
        icon: "phoneIncoming",
        color: "#22d3ee",
        items: [
          "24+ data points per call",
          "Real-time conversation alerts",
          "AI-powered transcription",
          "Performance benchmarking"
        ]
      }
    }
  },
  {
    id: 5,
    type: "calldrip",
    content: {
      label: "SPEED-TO-LEAD ENGINE",
      headline: "Never Miss Another Lead",
      features: [
        { icon: "zap", title: "Instant Connect", desc: "Route leads to the right rep in under 10 seconds" },
        { icon: "clock", title: "24/7 AI Assistants", desc: "Engage after-hours leads automatically" },
        { icon: "messageSquare", title: "Smart Follow-Up", desc: "40% more conversions with automated nurture" },
        { icon: "target", title: "Lead Scoring", desc: "Prioritize high-intent buyers first" },
      ],
      stat: { value: "33%", label: "of after-hours leads convert to appointments" }
    }
  },
  {
    id: 6,
    type: "callrevu",
    content: {
      label: "CALL ANALYTICS ENGINE",
      headline: "Turn Every Call Into Intelligence",
      features: [
        { icon: "brain", title: "AI Call Monitoring", desc: "Track 24+ data points from every conversation" },
        { icon: "bell", title: "Real-Time Alerts", desc: "Instant notifications for trades, pricing, issues" },
        { icon: "graduationCap", title: "Coaching Insights", desc: "Identify gaps and improve team performance" },
        { icon: "barChart", title: "Marketing ROI", desc: "Know exactly which channels drive real calls" },
      ],
      stat: { value: "143M+", label: "calls analyzed since 2020" }
    }
  },
  {
    id: 7,
    type: "alerts",
    content: {
      label: "REAL-TIME INTELLIGENCE",
      headline: "Know What's Happening Now",
      alertTypes: [
        { icon: "car", label: "Trade Mentions", desc: "Customer has a vehicle to trade", color: "#22c55e" },
        { icon: "dollar", label: "Pricing Concerns", desc: "Budget or price objections detected", color: "#f97316" },
        { icon: "alert", label: "Competitor References", desc: "Shopping other dealerships", color: "#ef4444" },
        { icon: "phone", label: "Missed Calls", desc: "Opportunities slipping away", color: "#8b5cf6" },
        { icon: "clock", label: "Long Hold Times", desc: "Customer patience running out", color: "#eab308" },
        { icon: "headphones", label: "Service Issues", desc: "Refund requests or complaints", color: "#ec4899" },
      ],
      punchline: "Intervene before the deal walks out the door"
    }
  },
  {
    id: 8,
    type: "departments",
    content: {
      label: "WHOLE DEALERSHIP COVERAGE",
      headline: "Sales. Service. Parts.",
      departments: [
        { 
          name: "Sales",
          color: "#3b82f6",
          metrics: ["Lead response time", "Appointment set rate", "Demo conversion", "Close rate"]
        },
        { 
          name: "Service",
          color: "#22c55e",
          metrics: ["Call answer rate", "Appointment booking", "Upsell mentions", "CSI indicators"]
        },
        { 
          name: "Parts",
          color: "#f97316",
          metrics: ["Inquiry handling", "Order conversion", "Hold time", "Customer satisfaction"]
        },
      ],
      punchline: "Benchmark everyone. Improve everything."
    }
  },
  {
    id: 9,
    type: "coaching",
    content: {
      label: "CONTINUOUS IMPROVEMENT",
      headline: "Turn Data Into Better Performance",
      process: [
        { step: "1", title: "Capture", desc: "Every call recorded and transcribed" },
        { step: "2", title: "Analyze", desc: "AI identifies patterns and gaps" },
        { step: "3", title: "Alert", desc: "Real-time coaching moments" },
        { step: "4", title: "Train", desc: "Targeted skill development" },
      ],
      result: "30% increase in sales performance with systematic coaching"
    }
  },
  {
    id: 10,
    type: "integration",
    content: {
      label: "UNIFIED PLATFORM",
      headline: "Everything Connected",
      integrations: [
        { label: "CRM Integration", desc: "Seamless data flow" },
        { label: "Phone System", desc: "Hosted or integrated" },
        { label: "Marketing Tools", desc: "Attribution tracking" },
        { label: "Reporting", desc: "Custom dashboards" },
      ],
      benefit: "No more data silos. One source of truth for phone intelligence."
    }
  },
  {
    id: 11,
    type: "results",
    content: {
      label: "PROVEN RESULTS",
      headline: "What Dealerships Are Seeing",
      results: [
        { value: "100%", label: "saw revenue increase with AI insights" },
        { value: "3x", label: "more appointments with speed-to-lead" },
        { value: "30%", label: "increase in used car sales" },
        { value: "40%", label: "better conversion with automated follow-up" },
      ],
      quote: "Every phone call contains the data needed to maximize sales."
    }
  },
  {
    id: 12,
    type: "trust",
    content: {
      label: "TRUSTED BY THOUSANDS",
      headline: "The Industry Standard",
      stats: [
        { value: "5,000+", label: "Dealerships" },
        { value: "15+", label: "Years Experience" },
        { value: "143M+", label: "Calls Analyzed" },
      ],
      logos: ["Automotive groups", "Multi-rooftop dealers", "Single-point stores"],
      punchline: "From single stores to the largest dealer groups in the country"
    }
  },
  {
    id: 13,
    type: "whyNow",
    content: {
      label: "WHY NOW",
      headline: "The Phone Still Matters",
      points: [
        { icon: "trending", text: "Digital leads are increasing, but calls close deals" },
        { icon: "users", text: "Customers expect instant, personalized response" },
        { icon: "target", text: "Your competitors are getting faster" },
        { icon: "brain", text: "AI makes real-time coaching possible" },
      ],
      punchline: "The dealerships that master phone intelligence will win."
    }
  },
  {
    id: 14,
    type: "cta",
    content: {
      headline: "Ready to Transform",
      highlight: "Every Call",
      subhead: "Into Revenue?",
      benefits: [
        "Respond faster than any competitor",
        "Know what's happening on every call",
        "Coach your team in real-time",
        "Measure what actually matters"
      ],
      closing: "Let's talk about your dealership."
    }
  }
];

// Icon component mapper
const IconComponent = ({ name, size = 24, className = "" }) => {
  const icons = {
    zap: Zap,
    phone: Phone,
    brain: Brain,
    target: Target,
    trending: TrendingUp,
    clock: Clock,
    users: Users,
    barChart: BarChart3,
    messageSquare: MessageSquare,
    shield: Shield,
    award: Award,
    check: CheckCircle,
    headphones: Headphones,
    bell: Bell,
    graduationCap: GraduationCap,
    car: Car,
    building: Building2,
    gauge: Gauge,
    lineChart: LineChart,
    phoneCall: PhoneCall,
    phoneIncoming: PhoneIncoming,
    phoneOutgoing: PhoneOutgoing,
    alert: AlertTriangle,
    dollar: DollarSign,
    rocket: Rocket,
  };
  const Icon = icons[name] || Target;
  return <Icon size={size} className={className} />;
};

// Slide Renderers
const TitleSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-12 opacity-90" />
    <h1 className="text-4xl md:text-6xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-7xl font-bold text-orange-500 mb-2">
      {content.highlight}
    </h1>
    <h2 className="text-4xl md:text-6xl font-light text-white mb-8">
      {content.subhead}
    </h2>
    <p className="text-xl text-cyan-400">{content.tagline}</p>
  </div>
);

const ProblemSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-6">
      {content.stats.map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-white/10 to-white/5 rounded-xl p-8 flex flex-col items-center justify-center border border-white/10">
          <span className="text-5xl font-bold text-orange-400 mb-3">{stat.value}</span>
          <span className="text-white/70 text-center">{stat.label}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl text-cyan-400 font-semibold">{content.punchline}</p>
    </div>
  </div>
);

const SpeedToLeadSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 space-y-6">
      {content.comparison.map((item, i) => (
        <div key={i} className="flex items-center gap-6">
          <div 
            className="w-40 py-4 px-6 rounded-lg text-white font-bold text-center"
            style={{ backgroundColor: item.color }}
          >
            {item.time}
          </div>
          <div className="flex-1 bg-white/5 rounded-lg p-5 border border-white/10">
            <span className="text-white text-xl">{item.result}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-3xl font-bold text-orange-500">{content.punchline}</p>
    </div>
  </div>
);

const TwoSidesSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-8">
      {[content.sideA, content.sideB].map((side, i) => (
        <div key={i} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
          <div className="p-5 flex items-center gap-4" style={{ backgroundColor: side.color }}>
            <IconComponent name={side.icon} size={32} className="text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">{side.title}</h3>
              <p className="text-white/80 text-sm">{side.subtitle}</p>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {side.items.map((item, j) => (
              <div key={j} className="flex items-center gap-3">
                <CheckCircle size={18} className="text-green-400 flex-shrink-0" />
                <span className="text-white/80">{item}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FeatureSlide = ({ content, accentColor = "#f97316" }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-6">
      {content.features.map((feature, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 flex items-start gap-4 border border-white/10">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accentColor + "30" }}>
            <IconComponent name={feature.icon} size={24} style={{ color: accentColor }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white mb-1">{feature.title}</h3>
            <p className="text-white/60">{feature.desc}</p>
          </div>
        </div>
      ))}
    </div>
    {content.stat && (
      <div className="mt-6 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-5 border border-cyan-500/30 flex items-center justify-center gap-4">
        <span className="text-4xl font-bold text-cyan-400">{content.stat.value}</span>
        <span className="text-white/80">{content.stat.label}</span>
      </div>
    )}
  </div>
);

const AlertsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-4">
      {content.alertTypes.map((alert, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-5 border border-white/10 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: alert.color + "30" }}>
            <IconComponent name={alert.icon} size={20} style={{ color: alert.color }} />
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm">{alert.label}</h4>
            <p className="text-white/50 text-xs">{alert.desc}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-6 text-center">
      <p className="text-2xl font-bold text-orange-500">{content.punchline}</p>
    </div>
  </div>
);

const DepartmentsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-3 gap-6">
      {content.departments.map((dept, i) => (
        <div key={i} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
          <div className="p-4 text-center" style={{ backgroundColor: dept.color }}>
            <h3 className="text-xl font-bold text-white">{dept.name}</h3>
          </div>
          <div className="p-5 space-y-2">
            {dept.metrics.map((metric, j) => (
              <div key={j} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color }} />
                <span className="text-white/70 text-sm">{metric}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-6 text-center">
      <p className="text-xl text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const CoachingSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex items-center justify-center">
      <div className="flex items-center gap-4">
        {content.process.map((item, i) => (
          <div key={i} className="flex items-center">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 w-48 text-center">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold mx-auto mb-3">
                {item.step}
              </div>
              <h4 className="text-lg font-bold text-white mb-1">{item.title}</h4>
              <p className="text-white/60 text-sm">{item.desc}</p>
            </div>
            {i < content.process.length - 1 && (
              <div className="text-orange-500 text-2xl mx-2">→</div>
            )}
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-5 border border-green-500/30 text-center">
      <p className="text-xl text-green-400 font-semibold">{content.result}</p>
    </div>
  </div>
);

const IntegrationSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-6 max-w-3xl mx-auto">
      {content.integrations.map((item, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 border border-white/10 flex items-center gap-4">
          <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
          <div>
            <h4 className="text-white font-semibold">{item.label}</h4>
            <p className="text-white/60 text-sm">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-xl text-cyan-400">{content.benefit}</p>
    </div>
  </div>
);

const ResultsSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-4 gap-6">
      {content.results.map((result, i) => (
        <div key={i} className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 rounded-xl p-6 flex flex-col items-center justify-center border border-green-500/30">
          <span className="text-4xl font-bold text-green-400 mb-2">{result.value}</span>
          <span className="text-white/70 text-center text-sm">{result.label}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-xl text-cyan-400 italic">"{content.quote}"</p>
    </div>
  </div>
);

const TrustSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="flex gap-12 mb-10">
        {content.stats.map((stat, i) => (
          <div key={i} className="text-center">
            <span className="text-5xl font-bold text-orange-400 block mb-2">{stat.value}</span>
            <span className="text-white/70">{stat.label}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        {content.logos.map((logo, i) => (
          <div key={i} className="bg-white/5 rounded-lg px-6 py-3 border border-white/10">
            <span className="text-white/60">{logo}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 text-center">
      <p className="text-xl text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const WhyNowSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-orange-500 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 space-y-5 max-w-3xl mx-auto">
      {content.points.map((point, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
          <IconComponent name={point.icon} size={28} className="text-orange-400 flex-shrink-0" />
          <span className="text-white text-lg">{point.text}</span>
        </div>
      ))}
    </div>
    <div className="mt-8 text-center">
      <p className="text-2xl font-bold text-cyan-400">{content.punchline}</p>
    </div>
  </div>
);

const CTASlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 mb-10 opacity-90" />
    <h1 className="text-4xl md:text-5xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-6xl font-bold text-orange-500 mb-2">
      {content.highlight}
    </h1>
    <h2 className="text-4xl md:text-5xl font-light text-white mb-10">
      {content.subhead}
    </h2>
    <div className="space-y-3 mb-10">
      {content.benefits.map((benefit, i) => (
        <div key={i} className="flex items-center gap-3 text-cyan-400">
          <CheckCircle size={20} />
          <span>{benefit}</span>
        </div>
      ))}
    </div>
    <p className="text-xl text-white/80">{content.closing}</p>
  </div>
);

// Main slide renderer
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    problem: ProblemSlide,
    speedToLead: SpeedToLeadSlide,
    twoSides: TwoSidesSlide,
    calldrip: (props) => <FeatureSlide {...props} accentColor="#f97316" />,
    callrevu: (props) => <FeatureSlide {...props} accentColor="#22d3ee" />,
    alerts: AlertsSlide,
    departments: DepartmentsSlide,
    coaching: CoachingSlide,
    integration: IntegrationSlide,
    results: ResultsSlide,
    trust: TrustSlide,
    whyNow: WhyNowSlide,
    cta: CTASlide,
  };
  
  const Renderer = renderers[slide.type];
  return Renderer ? <Renderer content={slide.content} /> : null;
};

export default function PresentationsPhoneIntel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, toggleFullscreen, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      {/* Slide Container */}
      <div className="flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2240] to-[#0a1628]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(249, 115, 22, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 20%, rgba(34, 211, 238, 0.06) 0%, transparent 40%)
            `
          }}
        />
        
        {/* Current Slide */}
        <div className="relative h-full">
          <SlideRenderer slide={slides[currentSlide]} />
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronLeft className="text-white" size={24} />
        </button>
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronRight className="text-white" size={24} />
        </button>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#050d18] border-t border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">
            Slide {currentSlide + 1} of {slides.length}
          </span>
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentSlide ? "bg-orange-500 w-6" : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/40 text-xs">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">F</kbd> for fullscreen
            <span className="mx-2">|</span>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 ml-1">→</kbd> to navigate
          </span>
          <PresentationDownloadButton
            slides={slides}
            renderSlide={(s) => <SlideRenderer slide={s} />}
            filename="CallRevu-Phone-Intel"
            bgColor="#0a1628"
          />
          <button
            onClick={toggleFullscreen}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {isFullscreen ? "Exit Fullscreen" : "Present"}
          </button>
        </div>
      </div>
    </div>
  );
}
